import React, { createContext, useState, useContext } from 'react';
import { BasicModal, TextButton } from 'components';
import { Row } from 'styles';

export interface AlertContextType {
  showAlert: (message: string, onOk?: () => void) => void;
  showConfirmAlert: (message: string, onOk?: () => void) => void;
  hideAlert: () => void;
}

const AlertContext = createContext<AlertContextType>({
  showAlert: (message) => {},
  showConfirmAlert: (message, onOk) => {},
  hideAlert: () => {}
});

interface Props {
  children: React.ReactNode;
}

interface IAlert {
  message: string;
  open: boolean;
  onOk?: () => void;
}

export const AlertProvider: React.FC<Props> = ({ children }) => {
  const [alert, setAlert] = useState<IAlert>({
    message: '',
    open: false
  });

  const [confirmAlert, setConfirmAlert] = useState<IAlert>({
    message: '',
    open: false
  });

  const showAlert = (message: string, onOk?: () => void) => {
    setAlert({ message, open: true, onOk });
  };

  const showConfirmAlert = (message: string, onOk?: () => void) => {
    setConfirmAlert({ message, open: true, onOk });
  };

  const hideAlert = () => {
    setConfirmAlert({ message: '', open: false, onOk: undefined });
    setAlert({ message: '', open: false, onOk: undefined });
  };

  const handleOk = () => {
    alert.onOk?.();
    hideAlert();
  };

  const handleConfirmOk = () => {
    confirmAlert.onOk?.();
    hideAlert();
  };

  return (
    <AlertContext.Provider value={{ showAlert, hideAlert, showConfirmAlert }}>
      {children}
      {
        <BasicModal
          header="Alert"
          open={alert.open}
          onClose={hideAlert}
          height={alert.message.length > 80 ? 230 : undefined}
          body={
            <Row style={{ justifyContent: 'center' }}>
              <div>{alert.message}</div>
            </Row>
          }
          footer={
            <Row>
              <TextButton label="OK" onClick={handleOk} />
            </Row>
          }
        />
      }
      {
        <BasicModal
          header="Alert"
          open={confirmAlert.open}
          height={confirmAlert.message.length > 80 ? 230 : undefined}
          onClose={hideAlert}
          body={
            <Row style={{ justifyContent: 'center' }}>
              <div>{confirmAlert.message}</div>
            </Row>
          }
          footer={
            <Row>
              <TextButton variant="outlined" label="Close" onClick={hideAlert} />
              <TextButton label="OK" onClick={handleConfirmOk} />
            </Row>
          }
        />
      }
    </AlertContext.Provider>
  );
};

// 커스텀 훅 useAlert 정의
export const useAlert = () => {
  const { showAlert, hideAlert, showConfirmAlert } = useContext(AlertContext);
  if (!showAlert || !hideAlert) {
    throw new Error('useAlert must be used within an AlertProvider');
  }

  return { showAlert, hideAlert, showConfirmAlert };
};
